import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const WEBAUDIO_SOURCE = 'https://www.w3.org/TR/webaudio/';

/**
 * AudioWorklet real-time thread jitter (novel_bot_detection §3A).
 *
 * Bare-metal desktops keep the real-time audio callback tightly bounded. VMs,
 * headless containers, and cloud instances show higher jitter variance and
 * distinct micro-burst scheduling caused by hypervisor interrupts and shared
 * CPU cores.
 *
 * WHAT WE MEASURE — and what an earlier revision got wrong: the processor runs
 * in its own real-time thread, so the only honest observable is WHEN its
 * messages ARRIVE on the main thread. The first implementation posted
 * `currentTime` and differenced it, but `currentTime` advances by exactly one
 * 128-frame render quantum per callback, so those deltas are a constant
 * (2.667 ms at 48 kHz) no matter how the thread is actually scheduled. We now
 * timestamp every worklet message with performance.now() on the main thread
 * and compute mean / sigma / p99 / burst count from the wall-clock arrival
 * gaps. The message payload (a sequence counter) is deliberately not used.
 *
 * The audio graph is resumed only long enough to collect samples, then
 * suspended; the processor writes silence, so nothing is audible. If the
 * autoplay policy keeps the context suspended (a context created without a
 * user gesture), we report `suspended` rather than null statistics.
 *
 * This is reported as a signal and never enters a fingerprint part: jitter
 * tracks real-time scheduling load, not device identity. It may raise the
 * `audio.high-jitter` finding; that finding's scoreContribution is 0 until
 * the threshold has been validated on real hardware.
 */

const WORKLET_SOURCE = `
class IntervalProbe extends AudioWorkletProcessor {
  constructor() {
    super();
    this.sent = 0;
  }
  process(inputs, outputs) {
    // Post a sequence counter once per render quantum. The VALUE is not used:
    // the main thread times arrival, because currentTime merely advances by a
    // fixed 128 frames per quantum and shows no scheduling jitter.
    if (this.sent < 400) {
      this.sent++;
      this.port.postMessage(this.sent);
    }
    // Keep the node alive with a silent output.
    const out = outputs[0];
    if (out && out[0]) out[0].fill(0);
    return true;
  }
}
registerProcessor('interval-probe', IntervalProbe);
`;

export const audioProbe: Probe = {
  id: 'audio',
  label: 'Audio thread jitter',
  layer: 'hardware',
  tier: 'client',
  category: 'hardware',
  description: 'AudioWorklet real-time callback jitter measured from message arrival',
  budgetMs: 3000,

  async run(): Promise<ProbeResult> {
    const hasAudioContext = typeof AudioContext === 'function' || typeof (globalThis as { webkitAudioContext?: unknown }).webkitAudioContext === 'function';
    const hasWorklet =
      typeof AudioWorkletNode === 'function';

    if (!hasAudioContext || !hasWorklet) {
      return {
        signals: {
          supported: na<boolean>('unsupported', 'client', 'AudioWorklet unavailable'),
        },
      };
    }

    try {
      const measurement = await measureJitter();

      if (!measurement.ok) {
        // Autoplay policy, headless without the right flags, or a context that
        // never reached 'running'. Say so plainly instead of reporting null
        // statistics as if they were a measurement.
        return {
          signals: {
            supported: na<string>(
              'suspended',
              'client',
              `AudioContext state=${measurement.state ?? 'unknown'} after resume (autoplay policy needs a user gesture?)`,
            ),
          },
        };
      }

      const stats = measurement.stats;

      // Virtualized environments show materially higher sigma. We use a
      // generous threshold (2 ms) because a loaded consumer machine can also
      // jitter. Reported as a signal; the finding is kept at
      // scoreContribution 0 until the threshold has been validated against
      // real VM and bare-metal runs.
      const findings: Finding[] = [];
      const virtualized = stats.sigmaMs != null && stats.sigmaMs > 2;

      if (virtualized) {
        findings.push({
          id: 'audio.high-jitter',
          severity: 'medium',
          category: 'hardware',
          layer: 'hardware',
          confidence: 'low',
          title: 'Audio real-time thread shows elevated jitter',
          detail:
            'Wall-clock arrival gaps between AudioWorklet callbacks on the main thread are wider than typical for bare-metal hardware. Virtual machines, containers, and headless environments frequently show this because the hypervisor and shared CPU scheduling interrupt the real-time thread. A heavily loaded physical machine can also jitter, and the threshold has not been validated across real VM/bare-metal pairs yet, so this is unscored supporting evidence rather than a conclusion.',
          sources: [WEBAUDIO_SOURCE],
          evidence: [
            `sigma=${stats.sigmaMs}ms`,
            `p99=${stats.p99Ms}ms`,
            `max=${stats.maxMs}ms`,
          ],
          scoreContribution: 0,
        });
      }

      return {
        signals: {
          supported: ok(true, 'client'),
          sampleCount: ok(stats.count, 'client'),
          meanArrivalMs: ok(stats.meanMs == null ? null : sig(stats.meanMs, 3), 'client'),
          sigmaArrivalMs: ok(stats.sigmaMs == null ? null : sig(stats.sigmaMs, 3), 'client'),
          p99ArrivalMs: ok(stats.p99Ms == null ? null : sig(stats.p99Ms, 3), 'client'),
          maxArrivalMs: ok(stats.maxMs == null ? null : sig(stats.maxMs, 3), 'client'),
          burstCount: ok(stats.bursts, 'client'),
          virtualizedLikely: ok(virtualized, 'derived'),
          note: ok(
            'Intervals are wall-clock arrival gaps on the main thread. The worklet clock advances by a fixed 128-frame quantum and is not used.',
            'derived',
          ),
        },
        findings,
        // No fingerprint part: audio jitter tracks real-time scheduling load,
        // not device identity, so it would drift between runs in the same
        // browser. It is reported as a signal and can still raise a finding.
      };
    } catch (err) {
      return {
        signals: {
          supported: na<boolean>('error', 'client', String((err as Error)?.message ?? err)),
        },
      };
    }
  },
};

export interface JitterStats {
  count: number;
  meanMs: number | null;
  sigmaMs: number | null;
  p99Ms: number | null;
  maxMs: number | null;
  bursts: number;
}

/**
 * Reduce a list of wall-clock inter-arrival gaps (in ms) to summary statistics.
 *
 * Pure and exported for unit tests. A "burst" is a gap that exceeds BOTH three
 * times the median AND 1 ms: the median arrival gap is often sub-millisecond
 * because several worklet messages are coalesced into one main-thread wake-up,
 * and counting those normal coalescing gaps as scheduler hiccups would be
 * misleading.
 */
export function jitterStatsFromGaps(gaps: number[]): JitterStats {
  const valid = gaps.filter((g) => Number.isFinite(g) && g >= 0);
  if (valid.length < 3) {
    return { count: valid.length, meanMs: null, sigmaMs: null, p99Ms: null, maxMs: null, bursts: 0 };
  }

  const mean = valid.reduce((a, b) => a + b, 0) / valid.length;
  const variance = valid.reduce((a, b) => a + (b - mean) ** 2, 0) / valid.length;
  const sigma = Math.sqrt(variance);
  const sorted = [...valid].sort((a, b) => a - b);
  const p99 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.99))];
  const max = sorted[sorted.length - 1];
  const median = sorted[Math.floor(sorted.length / 2)];
  const burstThreshold = Math.max(median * 3, 1);
  const bursts = valid.filter((g) => g > burstThreshold).length;

  return {
    count: valid.length,
    meanMs: mean,
    sigmaMs: sigma,
    p99Ms: p99,
    maxMs: max,
    bursts,
  };
}

type JitterMeasurement =
  | { ok: true; stats: JitterStats; state: string }
  | { ok: false; state: string | null };

async function measureJitter(): Promise<JitterMeasurement> {
  const Ctx = (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) throw new Error('no AudioContext');

  const ctx = new Ctx();
  const blob = new Blob([WORKLET_SOURCE], { type: 'application/javascript' });
  const url = URL.createObjectURL(blob);

  try {
    await ctx.audioWorklet.addModule(url);

    const node = new AudioWorkletNode(ctx, 'interval-probe', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });

    // Time ARRIVAL on the main thread; the message value is ignored.
    const arrivals: number[] = [];
    node.port.onmessage = () => {
      if (arrivals.length < 500) arrivals.push(performance.now());
    };

    // Do not await resume() alone: with an autoplay policy in force it can stay
    // pending indefinitely, which would burn the whole probe budget. Race it
    // against a short timer and then ask the context for its state.
    const resumed = ctx.resume().catch(() => undefined);
    await Promise.race([resumed, new Promise<void>((r) => setTimeout(r, 1000))]);

    if (ctx.state !== 'running') {
      // Capture the state BEFORE close(), which transitions it to 'closed'.
      const state = ctx.state;
      node.disconnect();
      await ctx.close().catch(() => undefined);
      return { ok: false, state };
    }

    // Collect for a short window. ~500 ms captures a few hundred callbacks.
    await new Promise<void>((resolve) => setTimeout(resolve, 500));

    const state = ctx.state;
    await ctx.suspend().catch(() => undefined);

    node.disconnect();
    await ctx.close().catch(() => undefined);

    // Derive inter-arrival gaps. A gap >= 250 ms means the page was suspended
    // or the context stalled; that is capture noise, not scheduling jitter.
    const gaps: number[] = [];
    for (let i = 1; i < arrivals.length; i++) {
      const d = arrivals[i] - arrivals[i - 1];
      if (d >= 0 && d < 250) gaps.push(d);
    }

    return { ok: true, stats: jitterStatsFromGaps(gaps), state };
  } finally {
    URL.revokeObjectURL(url);
  }
}

registerProbe(audioProbe);
